import {
  CheckCircleOutlined,
  DollarOutlined,
  FileTextOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  TeamOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import {
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { transportApi } from "../../api/transportApi";
import { getEnteredByUser } from "../../utils/currentUser";
import { getApiErrorMessage } from "../../utils/apiError";
import { StaticModal } from "../../shared/components/StaticModal";
import { StaticDrawer } from "../../shared/components/StaticDrawer";
import { SaveActionButton } from "../../shared/components/SaveActionButton";

const { Text, Title } = Typography;

// ── Helpers ────────────────────────────────────────────────────────────────

const statusColor: Record<string, string> = {
  Draft: "default",
  Calculated: "blue",
  Approved: "green",
  Paid: "purple",
  Cancelled: "red",
};

const fmtCcy = (v: number) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(v ?? 0);

const fmtDate = (v?: string | null) => (v ? dayjs(v).format("DD MMM YYYY") : "—");

// ── Help Modal ─────────────────────────────────────────────────────────────

const CommissionHelpModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => (
  <Modal
    open={open}
    onCancel={onClose}
    footer={<Button onClick={onClose}>Close</Button>}
    title={
      <Space>
        <QuestionCircleOutlined style={{ color: "#1890ff" }} />
        <span>How Commission Management Works</span>
      </Space>
    }
    width={760}
    styles={{ body: { maxHeight: "70vh", overflowY: "auto", paddingTop: 8 } }}
    destroyOnHidden
  >
    <Space direction="vertical" size={0} style={{ width: "100%" }}>

      <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
        Commission follows a <Text strong>5-step lifecycle</Text>: Set up Beneficiaries → Define Rules → Open a Period → Generate Statements → Approve &amp; Pay.
      </Text>

      {/* Step 1 */}
      <Divider orientation="left" orientationMargin={0}>
        <Tag color="blue">Step 1</Tag> Beneficiaries tab
      </Divider>
      <Text>
        A <Text strong>Beneficiary</Text> is anyone who earns commission — an employee, vendor, branch partner, or party.
        Register them once with their name, type, bank account, PAN, and IFSC. Deactivating a beneficiary stops
        them from appearing in future calculations.
      </Text>

      {/* Step 2 */}
      <Divider orientation="left" orientationMargin={0}>
        <Tag color="blue">Step 2</Tag> Commission Rules tab
      </Divider>
      <Text>
        A <Text strong>Rule</Text> defines how much to pay a beneficiary and on what basis.
        Each rule has an effective date range, so rules can change over time without affecting past periods.
      </Text>
      <div style={{ margin: "10px 0 4px" }}>
        <Text strong>Rule types:</Text>
      </div>
      <Table
        size="small"
        pagination={false}
        style={{ marginBottom: 8 }}
        dataSource={[
          { type: "Percentage", how: "Rate % × Basis Value (e.g. 2% of freight amount)" },
          { type: "Fixed Per Period", how: "One flat amount per period, regardless of consignment count" },
          { type: "Per Consignment", how: "Fixed rate × number of consignments booked" },
          { type: "Per Package", how: "Fixed rate × total packages across all consignments" },
          { type: "Per Weight", how: "Fixed rate × total weight (kg) across all consignments" },
        ]}
        columns={[
          { title: "Rule Type", dataIndex: "type", width: 180, render: (v: string) => <Tag>{v}</Tag> },
          { title: "How it calculates", dataIndex: "how" },
        ]}
      />
      <Text type="secondary" style={{ fontSize: 12 }}>
        For Percentage rules the <Text strong>Calculation Basis</Text> can be: Freight Amount, Declared Value, Package Count, Weight, or Consignment Count.
        A rule can optionally be scoped to a specific <Text strong>branch</Text>.
      </Text>

      {/* Step 3 */}
      <Divider orientation="left" orientationMargin={0}>
        <Tag color="blue">Step 3</Tag> Period Processing tab — Open a period
      </Divider>
      <Text>
        A <Text strong>Period</Text> represents one calendar month (e.g. APR-2026). Create one period per month.
        Duplicate periods for the same month and year are blocked.
      </Text>
      <div style={{ margin: "10px 0 4px" }}>
        <Text strong>Period lifecycle:</Text>
      </div>
      <Space wrap style={{ marginBottom: 4 }}>
        {["Draft", "Calculated", "Approved", "Closed"].map((s, i, arr) => (
          <Space key={s} size={4}>
            <Tag color={["default", "blue", "green", "purple"][i]}>{s}</Tag>
            {i < arr.length - 1 && <Text type="secondary">→</Text>}
          </Space>
        ))}
      </Space>

      {/* Step 4 */}
      <Divider orientation="left" orientationMargin={0}>
        <Tag color="blue">Step 4</Tag> Generate Statements
      </Divider>
      <Text>
        Click <Text strong>Generate</Text> on a period to run the calculation engine. What happens:
      </Text>
      <ol style={{ paddingLeft: 20, margin: "8px 0", lineHeight: "2" }}>
        <li>Existing Draft/Calculated statements are <Text strong>removed</Text> — re-generating is always safe.</li>
        <li>All <Text strong>active rules</Text> whose effective dates overlap the period are fetched.</li>
        <li>All <Text strong>non-cancelled consignments</Text> booked within the period date range are fetched.</li>
        <li>Rules are <Text strong>grouped by beneficiary</Text>. Each consignment is run through the formula.</li>
        <li>A <Text strong>Commission Statement</Text> (e.g. <Text code>CS/APR-2026/001</Text>) is created per beneficiary with status <Tag color="blue" style={{ margin: 0 }}>Calculated</Tag>.</li>
      </ol>
      <Text type="secondary" style={{ fontSize: 12 }}>
        You can re-generate any time the period is still Draft or Calculated — useful when consignments or rules were updated.
      </Text>

      {/* Step 5 */}
      <Divider orientation="left" orientationMargin={0}>
        <Tag color="blue">Step 5</Tag> Statements tab — Review, Adjust, Approve &amp; Pay
      </Divider>
      <Text>Each statement covers one beneficiary for one period. Available actions:</Text>
      <ul style={{ paddingLeft: 20, margin: "8px 0", lineHeight: "2.1" }}>
        <li><Text strong>View</Text> — see every consignment line, rule applied, basis value, rate, and amount.</li>
        <li><Text strong>Add Adjustment</Text> — add a deduction or addition with a reason (only before approval). Final payable updates live: <Text code>Gross + Adjustments − TDS = Final Payable</Text>.</li>
        <li><Text strong>Approve</Text> — locks the statement and freezes totals. Individual statements can be approved, or use <Text strong>Approve All</Text> at the period level to bulk-approve.</li>
        <li><Text strong>Record Payment</Text> — marks the statement <Tag color="purple" style={{ margin: 0 }}>Paid</Tag> with payment date, mode (NEFT/UPI/Cheque/RTGS), and reference number.</li>
        <li><Text strong>Cancel</Text> — cancels any statement that is not already paid.</li>
      </ul>

      {/* Closing the period */}
      <Divider orientation="left" orientationMargin={0}>
        <Tag color="purple">Final</Tag> Close the Period
      </Divider>
      <Text>
        Once all statements in a period are either <Tag color="purple" style={{ margin: 0 }}>Paid</Tag> or <Tag color="red" style={{ margin: 0 }}>Cancelled</Tag>,
        the <Text strong>Close Period</Text> button becomes available. Closing moves the period to final status — no further changes are possible.
      </Text>

      {/* Business rules */}
      <Divider orientation="left" orientationMargin={0}>
        <Text type="warning">Key Rules &amp; Restrictions</Text>
      </Divider>
      <ul style={{ paddingLeft: 20, margin: "4px 0", lineHeight: "2" }}>
        <li>Adjustments <Text strong>cannot</Text> be added to an Approved, Paid, or Cancelled statement.</li>
        <li>A statement <Text strong>must be Calculated</Text> before it can be approved.</li>
        <li>Payment can only be recorded on an <Text strong>Approved</Text> statement.</li>
        <li>A <Text strong>Paid</Text> statement cannot be cancelled.</li>
        <li>A period <Text strong>cannot be closed</Text> if any statement is still pending payment.</li>
      </ul>

    </Space>
  </Modal>
);

// ── Commission Page ────────────────────────────────────────────────────────

export const CommissionPage = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [helpOpen, setHelpOpen] = useState(false);

  return (
    <Card className="panel-card">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Commission Management
        </Title>
        <Button
          type="text"
          icon={<QuestionCircleOutlined style={{ fontSize: 18, color: "#1890ff" }} />}
          onClick={() => setHelpOpen(true)}
          title="How it works"
        />
      </div>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        destroyInactiveTabPane
        items={[
          { key: "dashboard", label: "Dashboard", icon: <ThunderboltOutlined />, children: <DashboardTab /> },
          { key: "beneficiaries", label: "Beneficiaries", icon: <TeamOutlined />, children: <BeneficiariesTab /> },
          { key: "rules", label: "Commission Rules", icon: <FileTextOutlined />, children: <RulesTab /> },
          { key: "periods", label: "Period Processing", icon: <ReloadOutlined />, children: <PeriodsTab /> },
          { key: "statements", label: "Statements", icon: <CheckCircleOutlined />, children: <StatementsTab /> },
          { key: "reports", label: "Reports", icon: <DollarOutlined />, children: <ReportsTab /> },
        ]}
      />
      <CommissionHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </Card>
  );
};

// ── Dashboard Tab ─────────────────────────────────────────────────────────

const DashboardTab = () => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await transportApi.getCommissionDashboard();
      setStats(data);
    } catch (e) {
      message.error(getApiErrorMessage(e, "Failed to load dashboard"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 24 }}>
        {[
          { title: "Total Statements", value: stats?.totalStatements ?? 0, color: "#1890ff" },
          { title: "Calculated", value: stats?.calculated ?? 0, color: "#1890ff" },
          { title: "Approved", value: stats?.approved ?? 0, color: "#52c41a" },
          { title: "Paid", value: stats?.paid ?? 0, color: "#722ed1" },
          { title: "Cancelled", value: stats?.cancelled ?? 0, color: "#ff4d4f" },
        ].map(({ title, value, color }) => (
          <Col key={title} xs={24} sm={12} md={8} lg={4}>
            <Card loading={loading} size="small" style={{ textAlign: "center" }}>
              <Statistic title={title} value={value} valueStyle={{ color, fontSize: 28 }} />
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={16}>
        {[
          { title: "Total Payable", value: stats?.totalPayable ?? 0 },
          { title: "Total Paid", value: stats?.totalPaid ?? 0 },
          { title: "Pending Payout", value: stats?.totalPending ?? 0 },
        ].map(({ title, value }) => (
          <Col key={title} xs={24} md={8}>
            <Card loading={loading} size="small" style={{ textAlign: "center" }}>
              <Statistic
                title={title}
                value={fmtCcy(value)}
                valueStyle={{ fontSize: 22 }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <div style={{ marginTop: 16, textAlign: "right" }}>
        <Button icon={<ReloadOutlined />} onClick={() => void load()} loading={loading}>
          Refresh
        </Button>
      </div>
    </div>
  );
};

// ── Beneficiaries Tab ─────────────────────────────────────────────────────

const BENEFICIARY_TYPES = [
  { value: "Employee", label: "Employee" },
  { value: "Vendor", label: "Vendor" },
  { value: "BranchPartner", label: "Branch Partner" },
  { value: "Party", label: "Party" },
];

const BeneficiariesTab = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form] = Form.useForm();

  const load = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const res = await transportApi.listCommissionBeneficiaries(q || undefined);
      setRows(res.items ?? []);
    } catch (e) {
      message.error(getApiErrorMessage(e, "Failed to load beneficiaries"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setOpen(true);
  };

  const openEdit = (row: any) => {
    setEditing(row);
    form.setFieldsValue({
      ...row,
      beneficiaryType: row.beneficiaryType,
    });
    setOpen(true);
  };

  const onSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const payload = { ...values, enteredBy: getEnteredByUser() };
      if (editing) {
        await transportApi.updateCommissionBeneficiary(editing.id, payload);
        message.success("Beneficiary updated");
      } else {
        await transportApi.createCommissionBeneficiary(payload);
        message.success("Beneficiary created");
      }
      setOpen(false);
      await load(search);
    } catch (e) {
      message.error(getApiErrorMessage(e, "Failed to save beneficiary"));
    } finally {
      setSaving(false);
    }
  };

  const onDeactivate = async (id: number) => {
    try {
      await transportApi.deactivateCommissionBeneficiary(id);
      message.success("Beneficiary deactivated");
      await load(search);
    } catch (e) {
      message.error(getApiErrorMessage(e, "Failed to deactivate"));
    }
  };

  const columns = [
    { title: "#", width: 50, render: (_: any, __: any, i: number) => i + 1 },
    { title: "Code", dataIndex: "beneficiaryCode", width: 100 },
    { title: "Name", dataIndex: "beneficiaryName" },
    { title: "Type", dataIndex: "beneficiaryType", width: 120,
      render: (v: string) => <Tag>{v}</Tag> },
    { title: "Phone", dataIndex: "contactPhone", width: 130 },
    { title: "Bank", dataIndex: "bankName", width: 120 },
    { title: "Account No", dataIndex: "bankAccountNo", width: 140 },
    { title: "Status", dataIndex: "isActive", width: 90,
      render: (v: boolean) => <Badge status={v ? "success" : "default"} text={v ? "Active" : "Inactive"} /> },
    { title: "", width: 130, render: (_: any, row: any) => (
      <Space>
        <Button size="small" onClick={() => openEdit(row)}>Edit</Button>
        {row.isActive && (
          <Button size="small" danger onClick={() => void onDeactivate(row.id)}>Deactivate</Button>
        )}
      </Space>
    )},
  ];

  return (
    <>
      <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 12 }} wrap>
        <Input.Search
          prefix={<SearchOutlined />}
          placeholder="Search beneficiaries..."
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSearch={(v) => { setSearch(v); void load(v); }}
          style={{ width: 320 }}
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add Beneficiary</Button>
      </Space>

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={rows}
        columns={columns}
        pagination={{ pageSize: 15, showSizeChanger: true }}
      />

      <StaticDrawer
        title={editing ? "Edit Beneficiary" : "Add Beneficiary"}
        open={open}
        onClose={() => setOpen(false)}
        width={640}
        footer={<div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}><Button onClick={() => setOpen(false)}>Cancel</Button><SaveActionButton saving={saving} idleText="Save" savingText="Saving..." onClick={() => void onSave()} /></div>}
        maskClosable={false}
      >
        <Form form={form} layout="vertical">
          <Row gutter={12}>
            <Col span={8}><Form.Item name="beneficiaryCode" label="Code" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={16}><Form.Item name="beneficiaryName" label="Name" rules={[{ required: true }]}><Input /></Form.Item></Col>
          </Row>
          <Form.Item name="beneficiaryType" label="Type" rules={[{ required: true }]}>
            <Select options={BENEFICIARY_TYPES} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="contactEmail" label="Email"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="contactPhone" label="Phone"><Input /></Form.Item></Col>
          </Row>
          <Divider orientation="left" plain>Bank / Payment Details</Divider>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="bankName" label="Bank Name"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="bankAccountNo" label="Account No"><Input /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="bankIfscCode" label="IFSC Code"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="panNo" label="PAN No"><Input /></Form.Item></Col>
          </Row>
          <Form.Item name="gstNo" label="GST No"><Input /></Form.Item>
          <Form.Item name="remarks" label="Remarks"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </StaticDrawer>
    </>
  );
};

// ── Rules Tab ─────────────────────────────────────────────────────────────

const RULE_TYPES = [
  { value: "Percentage", label: "Percentage (%)" },
  { value: "FixedPerPeriod", label: "Fixed Per Period" },
  { value: "PerConsignment", label: "Per Consignment" },
  { value: "PerPackage", label: "Per Package" },
  { value: "PerWeight", label: "Per Weight (kg)" },
];

const BASIS_OPTIONS = [
  { value: "FreightAmount", label: "Freight Amount" },
  { value: "DeclaredValue", label: "Declared Value" },
  { value: "PackageCount", label: "Package Count" },
  { value: "Weight", label: "Weight" },
  { value: "ConsignmentCount", label: "Consignment Count" },
];

const RulesTab = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [beneficiaries, setBeneficiaries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, bRes] = await Promise.all([
        transportApi.listCommissionRules(),
        transportApi.listCommissionBeneficiaries(),
      ]);
      setRows(rRes.items ?? []);
      setBeneficiaries(bRes.items ?? []);
    } catch (e) {
      message.error(getApiErrorMessage(e, "Failed to load rules"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ effectiveFrom: dayjs(), isActive: true });
    setOpen(true);
  };

  const openEdit = (row: any) => {
    setEditing(row);
    form.setFieldsValue({
      ...row,
      effectiveFrom: dayjs(row.effectiveFrom),
      effectiveTo: row.effectiveTo ? dayjs(row.effectiveTo) : null,
    });
    setOpen(true);
  };

  const onSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const payload = {
        ...values,
        effectiveFrom: values.effectiveFrom?.toISOString(),
        effectiveTo: values.effectiveTo?.toISOString() ?? null,
        enteredBy: getEnteredByUser(),
      };
      if (editing) {
        await transportApi.updateCommissionRule(editing.id, payload);
        message.success("Rule updated");
      } else {
        await transportApi.createCommissionRule(payload);
        message.success("Rule created");
      }
      setOpen(false);
      await load();
    } catch (e) {
      message.error(getApiErrorMessage(e, "Failed to save rule"));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: "#", width: 50, render: (_: any, __: any, i: number) => i + 1 },
    { title: "Rule Name", dataIndex: "ruleName" },
    { title: "Type", dataIndex: "ruleType", width: 140,
      render: (v: string) => <Tag color="blue">{v}</Tag> },
    { title: "Basis", dataIndex: "basis", width: 140 },
    { title: "Rate", dataIndex: "rate", width: 90, render: (v: number, row: any) =>
      row.ruleType === "Percentage" ? `${v}%` : fmtCcy(v) },
    { title: "Beneficiary", width: 160, render: (_: any, row: any) => row.beneficiary?.beneficiaryName ?? "—" },
    { title: "Effective From", dataIndex: "effectiveFrom", width: 120, render: fmtDate },
    { title: "To", dataIndex: "effectiveTo", width: 110, render: fmtDate },
    { title: "Active", dataIndex: "isActive", width: 70,
      render: (v: boolean) => <Badge status={v ? "success" : "default"} text={v ? "Yes" : "No"} /> },
    { title: "", width: 70, render: (_: any, row: any) => (
      <Button size="small" onClick={() => openEdit(row)}>Edit</Button>
    )},
  ];

  return (
    <>
      <div style={{ marginBottom: 12, textAlign: "right" }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add Rule</Button>
      </div>

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={rows}
        columns={columns}
        pagination={{ pageSize: 15 }}
      />

      <StaticDrawer
        title={editing ? "Edit Commission Rule" : "Add Commission Rule"}
        open={open}
        onClose={() => setOpen(false)}
        width={600}
        footer={<div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}><Button onClick={() => setOpen(false)}>Cancel</Button><SaveActionButton saving={saving} idleText="Save" savingText="Saving..." onClick={() => void onSave()} /></div>}
        maskClosable={false}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="ruleName" label="Rule Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="ruleType" label="Rule Type" rules={[{ required: true }]}>
                <Select options={RULE_TYPES} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="basis" label="Calculation Basis" rules={[{ required: true }]}>
                <Select options={BASIS_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="rate" label="Rate (% or Amount)" rules={[{ required: true }]}>
                <InputNumber style={{ width: "100%" }} min={0} precision={4} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="beneficiaryId" label="Beneficiary">
                <Select
                  allowClear
                  showSearch
                  placeholder="All beneficiaries"
                  options={beneficiaries.filter(b => b.isActive).map(b => ({ value: b.id, label: b.beneficiaryName }))}
                  filterOption={(inp, opt) => (opt?.label ?? "").toLowerCase().includes(inp.toLowerCase())}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="effectiveFrom" label="Effective From" rules={[{ required: true }]}>
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="effectiveTo" label="Effective To">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="remarks" label="Remarks"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </StaticDrawer>
    </>
  );
};

// ── Periods Tab ───────────────────────────────────────────────────────────

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
].map((m, i) => ({ value: i + 1, label: m }));

const PeriodsTab = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [actioning, setActioning] = useState<number | null>(null);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await transportApi.listCommissionPeriods();
      setRows(res.items ?? []);
    } catch (e) {
      message.error(getApiErrorMessage(e, "Failed to load periods"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const onCreatePeriod = async () => {
    const values = await form.validateFields();
    try {
      await transportApi.createCommissionPeriod({
        year: values.year,
        month: values.month,
        remarks: values.remarks,
        enteredBy: getEnteredByUser(),
      });
      message.success("Period created");
      setCreateOpen(false);
      form.resetFields();
      await load();
    } catch (e) {
      message.error(getApiErrorMessage(e, "Failed to create period"));
    }
  };

  const periodAction = async (id: number, action: "generate" | "approve" | "close") => {
    setActioning(id);
    try {
      const user = getEnteredByUser();
      if (action === "generate") await transportApi.generateCommissionPeriod(id, user);
      else if (action === "approve") await transportApi.approveCommissionPeriod(id, user);
      else await transportApi.closeCommissionPeriod(id, user);
      message.success(`Period ${action}d`);
      await load();
    } catch (e) {
      message.error(getApiErrorMessage(e, `Failed to ${action} period`));
    } finally {
      setActioning(null);
    }
  };

  const columns = [
    { title: "#", width: 50, render: (_: any, __: any, i: number) => i + 1 },
    { title: "Period", dataIndex: "periodCode", width: 110 },
    { title: "From", dataIndex: "periodFrom", width: 110, render: fmtDate },
    { title: "To", dataIndex: "periodTo", width: 110, render: fmtDate },
    { title: "Status", dataIndex: "status", width: 110,
      render: (v: string) => <Tag color={statusColor[v] ?? "default"}>{v}</Tag> },
    { title: "Generated By", dataIndex: "generatedBy", width: 130, render: (v: string | null) => v ?? "—" },
    { title: "Approved By", dataIndex: "approvedBy", width: 130, render: (v: string | null) => v ?? "—" },
    { title: "", width: 240, render: (_: any, row: any) => {
      const busy = actioning === row.id;
      return (
        <Space>
          {(row.status === "Draft" || row.status === "Calculated") && (
            <Button size="small" loading={busy} onClick={() => void periodAction(row.id, "generate")}>
              Generate
            </Button>
          )}
          {row.status === "Calculated" && (
            <Button size="small" type="primary" loading={busy} onClick={() => void periodAction(row.id, "approve")}>
              Approve All
            </Button>
          )}
          {row.status === "Approved" && (
            <Button size="small" loading={busy} onClick={() => void periodAction(row.id, "close")}>
              Close Period
            </Button>
          )}
        </Space>
      );
    }},
  ];

  return (
    <>
      <div style={{ marginBottom: 12, textAlign: "right" }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          New Period
        </Button>
      </div>

      <Table rowKey="id" size="small" loading={loading} dataSource={rows} columns={columns} pagination={{ pageSize: 12 }} />

      <StaticModal
        title="Create Commission Period"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => void onCreatePeriod()}
        width={420}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" initialValues={{ year: dayjs().year() }}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="month" label="Month" rules={[{ required: true }]}>
                <Select options={MONTHS} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="year" label="Year" rules={[{ required: true }]}>
                <InputNumber style={{ width: "100%" }} min={2020} max={2099} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="remarks" label="Remarks"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </StaticModal>
    </>
  );
};

// ── Statements Tab ────────────────────────────────────────────────────────

const PAYMENT_MODES = ["Cash", "Bank Transfer", "NEFT", "RTGS", "Cheque", "UPI"].map(m => ({ value: m, label: m }));

const StatementsTab = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string | undefined>(undefined);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [adjOpen, setAdjOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [adjForm] = Form.useForm();
  const [payForm] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await transportApi.listCommissionStatements(
        filterStatus ? { status: filterStatus } : undefined
      );
      setRows(res.items ?? []);
    } catch (e) {
      message.error(getApiErrorMessage(e, "Failed to load statements"));
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { void load(); }, [load]);

  const openDetail = async (row: any) => {
    const detail = await transportApi.getCommissionStatement(row.id);
    setSelected(detail);
    setDetailOpen(true);
  };

  const approve = async (id: number) => {
    setSaving(true);
    try {
      await transportApi.approveCommissionStatement(id, getEnteredByUser());
      message.success("Statement approved");
      await load();
      setDetailOpen(false);
    } catch (e) {
      message.error(getApiErrorMessage(e, "Failed to approve"));
    } finally {
      setSaving(false);
    }
  };

  const cancel = async (id: number) => {
    Modal.confirm({
      title: "Cancel Statement?",
      content: "This will mark the statement as Cancelled. Are you sure?",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await transportApi.cancelCommissionStatement(id, getEnteredByUser());
          message.success("Statement cancelled");
          await load();
          setDetailOpen(false);
        } catch (e) {
          message.error(getApiErrorMessage(e, "Failed to cancel"));
        }
      }
    });
  };

  const onAddAdjustment = async () => {
    const values = await adjForm.validateFields();
    setSaving(true);
    try {
      await transportApi.addCommissionAdjustment(selected.id, {
        ...values,
        enteredBy: getEnteredByUser(),
      });
      message.success("Adjustment added");
      const updated = await transportApi.getCommissionStatement(selected.id);
      setSelected(updated);
      setAdjOpen(false);
      adjForm.resetFields();
    } catch (e) {
      message.error(getApiErrorMessage(e, "Failed to add adjustment"));
    } finally {
      setSaving(false);
    }
  };

  const onRecordPayment = async () => {
    const values = await payForm.validateFields();
    setSaving(true);
    try {
      await transportApi.recordCommissionPayment(selected.id, {
        paidBy: getEnteredByUser(),
        paidOn: values.paidOn?.toISOString(),
        paymentReference: values.paymentReference,
        paymentMode: values.paymentMode,
      });
      message.success("Payment recorded");
      await load();
      setPayOpen(false);
      setDetailOpen(false);
    } catch (e) {
      message.error(getApiErrorMessage(e, "Failed to record payment"));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: "#", width: 50, render: (_: any, __: any, i: number) => i + 1 },
    { title: "Statement No", dataIndex: "statementNo", width: 160 },
    { title: "Period", width: 110, render: (_: any, row: any) => row.period?.periodCode ?? "—" },
    { title: "Beneficiary", width: 160, render: (_: any, row: any) => row.beneficiary?.beneficiaryName ?? "—" },
    { title: "Gross", dataIndex: "grossAmount", width: 110, render: fmtCcy },
    { title: "Adj", dataIndex: "adjustmentAmount", width: 100,
      render: (v: number) => <Text type={v < 0 ? "danger" : v > 0 ? "success" : "secondary"}>{fmtCcy(v)}</Text> },
    { title: "Final Payable", dataIndex: "finalPayable", width: 120, render: (v: number) => <Text strong>{fmtCcy(v)}</Text> },
    { title: "Status", dataIndex: "status", width: 110,
      render: (v: string) => <Tag color={statusColor[v] ?? "default"}>{v}</Tag> },
    { title: "Paid On", dataIndex: "paidOn", width: 110, render: fmtDate },
    { title: "", width: 80, render: (_: any, row: any) => (
      <Button size="small" onClick={() => void openDetail(row)}>View</Button>
    )},
  ];

  return (
    <>
      <Space style={{ marginBottom: 12 }}>
        <Text>Filter by status:</Text>
        <Select
          allowClear
          placeholder="All statuses"
          style={{ width: 160 }}
          value={filterStatus}
          onChange={(v) => setFilterStatus(v)}
          options={["Draft","Calculated","Approved","Paid","Cancelled"].map(s => ({ value: s, label: s }))}
        />
        <Button onClick={() => void load()} icon={<ReloadOutlined />}>Refresh</Button>
      </Space>

      <Table rowKey="id" size="small" loading={loading} dataSource={rows} columns={columns} pagination={{ pageSize: 15 }} />

      {/* Statement Detail Drawer */}
      <StaticDrawer
        title={`Statement — ${selected?.statementNo ?? ""}`}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        width={820}
        extra={
          <Space>
            {selected?.status === "Calculated" && (
              <Button type="primary" loading={saving} onClick={() => void approve(selected.id)}>
                Approve
              </Button>
            )}
            {selected?.status === "Approved" && (
              <Button type="primary" onClick={() => { payForm.resetFields(); payForm.setFieldsValue({ paidOn: dayjs() }); setPayOpen(true); }}>
                Record Payment
              </Button>
            )}
            {(selected?.status === "Calculated" || selected?.status === "Draft") && (
              <Button onClick={() => { adjForm.resetFields(); setAdjOpen(true); }}>Add Adjustment</Button>
            )}
            {selected?.status !== "Paid" && selected?.status !== "Cancelled" && (
              <Button danger onClick={() => void cancel(selected.id)}>Cancel</Button>
            )}
          </Space>
        }
      >
        {selected && (
          <>
            <Descriptions bordered size="small" column={3}>
              <Descriptions.Item label="Statement No">{selected.statementNo}</Descriptions.Item>
              <Descriptions.Item label="Period">{selected.period?.periodCode}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={statusColor[selected.status] ?? "default"}>{selected.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Beneficiary">{selected.beneficiary?.beneficiaryName}</Descriptions.Item>
              <Descriptions.Item label="Gross Amount">{fmtCcy(selected.grossAmount)}</Descriptions.Item>
              <Descriptions.Item label="Adjustments">{fmtCcy(selected.adjustmentAmount)}</Descriptions.Item>
              <Descriptions.Item label="Net Payable">{fmtCcy(selected.netPayable)}</Descriptions.Item>
              <Descriptions.Item label="TDS">{fmtCcy(selected.tdsAmount)}</Descriptions.Item>
              <Descriptions.Item label="Final Payable"><Text strong>{fmtCcy(selected.finalPayable)}</Text></Descriptions.Item>
              {selected.approvedBy && <Descriptions.Item label="Approved By">{selected.approvedBy} on {fmtDate(selected.approvedOn)}</Descriptions.Item>}
              {selected.paidBy && <Descriptions.Item label="Paid By">{selected.paidBy} on {fmtDate(selected.paidOn)}</Descriptions.Item>}
              {selected.paymentReference && <Descriptions.Item label="Payment Ref">{selected.paymentReference} ({selected.paymentMode})</Descriptions.Item>}
            </Descriptions>

            <Divider orientation="left">Commission Lines</Divider>
            <Table
              rowKey="id"
              size="small"
              dataSource={selected.lines ?? []}
              pagination={false}
              columns={[
                { title: "Consignment", dataIndex: "consignmentNo", width: 130, render: (v: string | null) => v ?? "—" },
                { title: "Rule", width: 160, render: (_: any, r: any) => r.rule?.ruleName ?? r.ruleId },
                { title: "Basis", dataIndex: "basis", width: 120 },
                { title: "Basis Value", dataIndex: "basisValue", width: 100, render: (v: number) => v.toFixed(2) },
                { title: "Rate", dataIndex: "rate", width: 80, render: (v: number, r: any) =>
                  r.ruleType === "Percentage" ? `${v}%` : fmtCcy(v) },
                { title: "Amount", dataIndex: "calculatedAmount", width: 110, render: fmtCcy },
                { title: "Remarks", dataIndex: "lineRemarks", render: (v: string | null) => v ?? "" },
              ]}
            />

            {(selected.adjustments ?? []).length > 0 && (
              <>
                <Divider orientation="left">Adjustments</Divider>
                <Table
                  rowKey="id"
                  size="small"
                  dataSource={selected.adjustments ?? []}
                  pagination={false}
                  columns={[
                    { title: "Type", dataIndex: "adjustmentType", width: 100,
                      render: (v: string, r: any) => <Tag color={r.isVoided ? "default" : v === "Addition" ? "green" : "red"}>{v}</Tag> },
                    { title: "Amount", dataIndex: "amount", width: 100,
                      render: (v: number, r: any) => <Text delete={r.isVoided}>{fmtCcy(v)}</Text> },
                    { title: "Reason", dataIndex: "reason" },
                    { title: "By", dataIndex: "enteredBy", width: 120 },
                    { title: "Voided", dataIndex: "isVoided", width: 70,
                      render: (v: boolean) => v ? <Tag color="default">Voided</Tag> : null },
                  ]}
                />
              </>
            )}
          </>
        )}
      </StaticDrawer>

      {/* Add Adjustment Modal */}
      <StaticModal
        title="Add Adjustment"
        open={adjOpen}
        onCancel={() => setAdjOpen(false)}
        onOk={() => void onAddAdjustment()}
        confirmLoading={saving}
        width={420}
        destroyOnHidden
      >
        <Form form={adjForm} layout="vertical">
          <Form.Item name="adjustmentType" label="Type" rules={[{ required: true }]}>
            <Select options={[{ value: "Addition", label: "Addition (+)" }, { value: "Deduction", label: "Deduction (−)" }]} />
          </Form.Item>
          <Form.Item name="amount" label="Amount" rules={[{ required: true }]}>
            <InputNumber style={{ width: "100%" }} min={0.01} precision={2} />
          </Form.Item>
          <Form.Item name="reason" label="Reason" rules={[{ required: true }]}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </StaticModal>

      {/* Record Payment Modal */}
      <StaticModal
        title="Record Payment"
        open={payOpen}
        onCancel={() => setPayOpen(false)}
        onOk={() => void onRecordPayment()}
        confirmLoading={saving}
        width={420}
        destroyOnHidden
      >
        <Form form={payForm} layout="vertical">
          <Form.Item name="paidOn" label="Payment Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="paymentMode" label="Payment Mode" rules={[{ required: true }]}>
            <Select options={PAYMENT_MODES} />
          </Form.Item>
          <Form.Item name="paymentReference" label="Reference / UTR">
            <Input />
          </Form.Item>
        </Form>
      </StaticModal>
    </>
  );
};

// ── Reports Tab ───────────────────────────────────────────────────────────

const ReportsTab = () => {
  const [reportType, setReportType] = useState<"monthly" | "payable" | "paidvs">("monthly");
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(dayjs().year());
  const [month, setMonth] = useState(dayjs().month() + 1);

  const runReport = useCallback(async () => {
    setLoading(true);
    try {
      let res: any;
      if (reportType === "monthly") res = await transportApi.getCommissionMonthlyRegister(year, month);
      else if (reportType === "payable") res = await transportApi.getCommissionBeneficiaryPayable();
      else res = await transportApi.getCommissionPaidVsUnpaid();
      setRows(res.items ?? []);
    } catch (e) {
      message.error(getApiErrorMessage(e, "Failed to run report"));
    } finally {
      setLoading(false);
    }
  }, [reportType, year, month]);

  const monthlyColumns = [
    { title: "Statement No", dataIndex: "statementNo" },
    { title: "Beneficiary", dataIndex: "beneficiaryName" },
    { title: "Type", dataIndex: "beneficiaryType" },
    { title: "Gross", dataIndex: "grossAmount", render: fmtCcy },
    { title: "Adj", dataIndex: "adjustmentAmount", render: fmtCcy },
    { title: "Final Payable", dataIndex: "finalPayable", render: fmtCcy },
    { title: "Status", dataIndex: "status", render: (v: string) => <Tag color={statusColor[v] ?? "default"}>{v}</Tag> },
    { title: "Approved By", dataIndex: "approvedBy", render: (v: string | null) => v ?? "—" },
    { title: "Paid On", dataIndex: "paidOn", render: fmtDate },
    { title: "Ref", dataIndex: "paymentReference", render: (v: string | null) => v ?? "—" },
  ];

  const payableColumns = [
    { title: "Statement No", dataIndex: "statementNo" },
    { title: "Period", dataIndex: "periodCode" },
    { title: "Beneficiary", dataIndex: "beneficiaryName" },
    { title: "Final Payable", dataIndex: "finalPayable", render: fmtCcy },
    { title: "Status", dataIndex: "status", render: (v: string) => <Tag color={statusColor[v] ?? "default"}>{v}</Tag> },
    { title: "Paid On", dataIndex: "paidOn", render: fmtDate },
    { title: "Ref", dataIndex: "paymentReference", render: (v: string | null) => v ?? "—" },
  ];

  const paidVsColumns = [
    { title: "Period", dataIndex: "periodCode" },
    { title: "Total", dataIndex: "totalStatements" },
    { title: "Paid", dataIndex: "paidCount", render: (v: number) => <Text style={{ color: "#52c41a" }}>{v}</Text> },
    { title: "Unpaid", dataIndex: "unpaidCount", render: (v: number) => <Text type="warning">{v}</Text> },
    { title: "Total Payable", dataIndex: "totalPayable", render: fmtCcy },
    { title: "Total Paid", dataIndex: "totalPaid", render: fmtCcy },
    { title: "Pending", dataIndex: "totalPending", render: (v: number) => <Text type="danger">{fmtCcy(v)}</Text> },
  ];

  return (
    <>
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          value={reportType}
          onChange={(v) => setReportType(v)}
          style={{ width: 240 }}
          options={[
            { value: "monthly", label: "Monthly Branch-wise Register" },
            { value: "payable", label: "Beneficiary-wise Payable" },
            { value: "paidvs", label: "Paid vs Unpaid Summary" },
          ]}
        />
        {reportType === "monthly" && (
          <>
            <Select value={month} onChange={setMonth} style={{ width: 130 }} options={MONTHS} />
            <InputNumber value={year} onChange={(v) => v && setYear(v)} min={2020} max={2099} style={{ width: 90 }} />
          </>
        )}
        <Button type="primary" loading={loading} onClick={() => void runReport()}>Run Report</Button>
      </Space>

      <Table
        rowKey={(_, i) => String(i)}
        size="small"
        loading={loading}
        dataSource={rows}
        columns={reportType === "monthly" ? monthlyColumns : reportType === "payable" ? payableColumns : paidVsColumns}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        summary={(pageData) => {
          if (!pageData.length) return null;
          const total = pageData.reduce((s: number, r: any) => s + (r.finalPayable ?? r.totalPayable ?? 0), 0);
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={reportType === "monthly" ? 4 : 3}>
                <Text strong>Page Total</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1}>
                <Text strong>{fmtCcy(total)}</Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          );
        }}
      />
    </>
  );
};
